import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  DollarSign,
  RefreshCw,
  TrendingUp,
  Calendar,
  CheckCircle,
  Clock,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Badge, Button, Select } from '../../components/ui';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';

interface CommissionRecord {
  id: string;
  operationId: string;
  operationType: string;
  feeCharged: number;
  commissionPercentage: number;
  commissionAmount: number;
  status: 'PENDING' | 'PAID';
  createdAt: string;
  paidAt?: string;
}

interface CommissionSummary {
  total: number;
  pending: number;
  paid: number;
  byOperation: Record<string, number>;
}

export function ManagerCommissions() {
  const [period, setPeriod] = useState('30d');

  const { data: summary, isLoading: summaryLoading, refetch: refetchSummary } = useQuery({
    queryKey: ['manager-commissions-summary', period],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.managerCommissions.summary, { params: { period } });
      return response.data as CommissionSummary;
    },
  });

  const { data: commissions, isLoading: commissionsLoading } = useQuery({
    queryKey: ['manager-commissions', period],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.managerCommissions.myCommissions, { params: { period } });
      return response.data as CommissionRecord[];
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value / 100);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const operationLabels: Record<string, string> = {
    PIX_SEND: 'Pix Enviar',
    PIX_RECEIVE: 'Pix Receber',
    TRANSFER: 'Transferência',
    BOLETO: 'Boleto',
    WITHDRAWAL: 'Saque',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Minhas Comissões</h1>
          <p className="text-surface-400">Acompanhe suas comissões sobre as operações do tenant</p>
        </div>
        <div className="flex items-center gap-4">
          <Select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            options={[
              { value: '7d', label: 'Últimos 7 dias' },
              { value: '30d', label: 'Últimos 30 dias' },
              { value: '90d', label: 'Últimos 90 dias' },
              { value: '365d', label: 'Último ano' },
            ]}
          />
          <Button variant="secondary" onClick={() => refetchSummary()} disabled={summaryLoading}>
            <RefreshCw className={`w-4 h-4 ${summaryLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card variant="stat">
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-surface-400 text-sm">Total de Comissões</p>
                {summaryLoading ? (
                  <div className="h-8 w-32 bg-surface-700 animate-pulse rounded mt-1" />
                ) : (
                  <p className="text-2xl font-bold text-surface-100">
                    {formatCurrency(summary?.total || 0)}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-xl bg-primary-500/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-primary-400" />
              </div>
            </div>
          </CardBody>
        </Card>

        <Card variant="stat">
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-surface-400 text-sm">Pendente</p>
                {summaryLoading ? (
                  <div className="h-8 w-32 bg-surface-700 animate-pulse rounded mt-1" />
                ) : (
                  <p className="text-2xl font-bold text-yellow-400">
                    {formatCurrency(summary?.pending || 0)}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-400" />
              </div>
            </div>
          </CardBody>
        </Card>

        <Card variant="stat">
          <CardBody>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-surface-400 text-sm">Pago</p>
                {summaryLoading ? (
                  <div className="h-8 w-32 bg-surface-700 animate-pulse rounded mt-1" />
                ) : (
                  <p className="text-2xl font-bold text-green-400">
                    {formatCurrency(summary?.paid || 0)}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Commissions by Operation */}
      {summary?.byOperation && Object.keys(summary.byOperation).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary-400" />
              Por Tipo de Operação
            </CardTitle>
          </CardHeader>
          <CardBody className="p-0">
            <div className="divide-y divide-surface-800">
              {Object.entries(summary.byOperation).map(([operation, amount]) => (
                <div
                  key={operation}
                  className="flex items-center justify-between px-6 py-4"
                >
                  <span className="text-surface-300">
                    {operationLabels[operation] || operation}
                  </span>
                  <span className="text-primary-400 font-medium">{formatCurrency(amount)}</span>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}

      {/* Commission Records */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary-400" />
            Histórico de Comissões
          </CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {commissionsLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="animate-pulse bg-surface-800 rounded h-16" />
              ))}
            </div>
          ) : !commissions || commissions.length === 0 ? (
            <div className="text-center py-12 text-surface-400">
              <DollarSign className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhuma comissão no período</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-surface-800/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase">Data</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-surface-400 uppercase">Operação</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-surface-400 uppercase">Taxa Cobrada</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-surface-400 uppercase">%</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-surface-400 uppercase">Comissão</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-surface-400 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-800">
                  {commissions.map((commission) => (
                    <tr key={commission.id} className="hover:bg-surface-800/30">
                      <td className="px-6 py-4 text-surface-300 text-sm">
                        {formatDate(commission.createdAt)}
                      </td>
                      <td className="px-6 py-4 text-surface-100">
                        {operationLabels[commission.operationType] || commission.operationType}
                      </td>
                      <td className="px-6 py-4 text-right text-surface-300">
                        {formatCurrency(commission.feeCharged)}
                      </td>
                      <td className="px-6 py-4 text-right text-surface-400">
                        {commission.commissionPercentage}%
                      </td>
                      <td className="px-6 py-4 text-right text-primary-400 font-medium">
                        {formatCurrency(commission.commissionAmount)}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <Badge variant={commission.status === 'PAID' ? 'success' : 'warning'}>
                          {commission.status === 'PAID' ? 'Pago' : 'Pendente'}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </motion.div>
  );
}
